import React, { FC, useState } from "react";
import { BaseWidgetProps, WidgetWrapper } from "shared-ui";
import {
  Box,
  Button,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../utils";
import { DateTime } from "luxon";
import ManufacturerLogo from "shared-ui/src/components/ManufacturerLogo";
import {
  GetUpcomingRenewalsResponse,
  LifecycleManagementAllEvents,
  LifecycleManagementClient,
  UpcomingRenewalsResponseItem,
} from "@lib/ShiOneClient";
import ms from "ms";
import { AssetLifecycleManagementDetailDrawer } from "@features/assets-feature/components/asset-lifecycle-management/AssetLifecycleManagementDetailDrawer";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { usdWholeDollarFormatter } from "shared-ui/src/functions/currency";
import { useGlobalOverlayBasePath } from "shared-ui/src/hooks/routedOverlays";
import { LIFECYCLE_SEGMENTS } from "@features/assets-feature/routing/lifecycleRouteSegments";
import { useLifecycleEventDrawerRoute } from "@features/assets-feature/components/asset-lifecycle-management/RoutedLifecycleEventDrawer";
import { isLifecycleEventWidgetHost } from "./upcomingRenewalsRouting";

const termMonthsOptions = [
  { label: "Next 12 Months", value: 12 },
  { label: "Next 24 Months", value: 24 },
  { label: "Next 36 Months", value: 36 },
];

const maxRenewalItems = 10;
const RenewalListItem: FC<{
  renewal: UpcomingRenewalsResponseItem;
  onClick: (renewal: UpcomingRenewalsResponseItem) => void;
}> = ({ renewal, onClick }) => {
  const theme = useTheme();

  return (
    <ListItemButton
      sx={{
        borderTop: `solid 1px ${theme.palette.divider}`,
        padding: theme.spacing(2),
        display: "flex",
        gap: 2,
      }}
      onClick={() => onClick(renewal)}
    >
      <ManufacturerLogo
        manufacturer={renewal.manufacturer}
        size="large"
        baseUrl={import.meta.env.LOGOS}
        hidePlaceholder
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" fontWeight="medium">
          {renewal.manufacturer}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {renewal.assetQuantity} {renewal.itemType}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
        }}
      >
        <Typography variant="body2" fontWeight="medium">
          {renewal.compellingEventDate
            ? DateTime.fromJSDate(renewal.compellingEventDate, {
                zone: "utc",
              }).toFormat("M/d/yyyy")
            : "—"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "success.main",
          }}
        >
          {renewal.previousPurchaseCost == null
            ? ""
            : usdWholeDollarFormatter.format(renewal.previousPurchaseCost)}
        </Typography>
      </Box>
      <ChevronRightIcon />
    </ListItemButton>
  );
};

const useGetTopUpcomingRenewals = (termMonths: number) => {
  const api = useApi(LifecycleManagementClient);

  return useQuery<GetUpcomingRenewalsResponse, Error>({
    queryKey: ["top-upcoming-renewals", termMonths],
    queryFn: () => api.getUpcomingRenewals(maxRenewalItems, termMonths),
    staleTime: ms("1m"),
  });
};

const UpcomingRenewalsWidget: FC<BaseWidgetProps> = ({ pageLayoutWidget }) => {
  const navigate = useNavigate();
  const [termMonths, setTermMonths] = useState(12);
  const { data, isLoading, isError } = useGetTopUpcomingRenewals(termMonths);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedLifecycleEventData, setSelectedLifecycleEventData] = useState<
    LifecycleManagementAllEvents | undefined
  >();
  const lifecycleApi = useApi(LifecycleManagementClient);
  const { hasPermission } = useClaims();
  const hasAssetOrderDataManagePermission = hasPermission(
    ShiOnePermission.assetOrderDataManage,
  );
  // On host dashboards the routed drawer is mounted by `UpcomingRenewalsEventDrawerHost`;
  // elsewhere it opens locally.
  const overlayBasePath = useGlobalOverlayBasePath(LIFECYCLE_SEGMENTS.event);
  const isRoutedHost = isLifecycleEventWidgetHost(overlayBasePath);
  const eventDrawerRoute = useLifecycleEventDrawerRoute(overlayBasePath);

  const headerAction = () => {
    return (
      <Select
        sx={{ height: "40px" }}
        labelId="TermSelector"
        id="TermSelector"
        value={termMonths}
        onChange={(event) => {
          setTermMonths(
            Number(event.target.value) ? Number(event.target.value) : 0,
          );
        }}
        autoWidth
        displayEmpty
      >
        {termMonthsOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const renewals = (data?.items || []).sort((a, b) => {
    if (!a.compellingEventDate) return 1;
    if (!b.compellingEventDate) return -1;
    return a.compellingEventDate.getTime() - b.compellingEventDate.getTime();
  });
  const displayedRenewals = renewals.slice(0, 5);

  const { mutate: fetchEventDetails } = useMutation({
    mutationFn: (identifier: string) =>
      lifecycleApi.getEventByUniqueIdentifier(identifier),
    onSuccess: (event) => {
      setSelectedLifecycleEventData(event);
      setDetailDrawerOpen(true);
    },
  });

  const handleRenewalClick = (renewal: UpcomingRenewalsResponseItem) => {
    if (!renewal.identifier) return;
    if (isRoutedHost && eventDrawerRoute.canRoute(renewal.identifier)) {
      eventDrawerRoute.open(renewal.identifier);
      return;
    }
    fetchEventDetails(renewal.identifier);
  };

  const handleViewAllClick = () => {
    navigate(
      "/assets/lifecycle-management?tab=event-timeline&eventType=Renewal",
    );
  };

  const cardActions = () => {
    return (
      <Button variant="text" color="primary" onClick={handleViewAllClick}>
        VIEW ALL RENEWALS
      </Button>
    );
  };

  return (
    <WidgetWrapper
      pageLayoutWidget={pageLayoutWidget}
      noPadding={true}
      error={isError}
      isEmpty={displayedRenewals.length === 0}
      cardActions={cardActions()}
      headerAction={headerAction()}
    >
      <List
        sx={{
          padding: 0,
          maxHeight: displayedRenewals.length > 5 ? "400px" : "none",
          overflowY: displayedRenewals.length > 5 ? "auto" : "visible",
        }}
      >
        {isLoading ? (
          <Grid container p={2} flexDirection={"column"} gap={1}>
            <Skeleton />
            <Skeleton />
          </Grid>
        ) : displayedRenewals.length > 0 ? (
          displayedRenewals.map((renewal, index) => (
            <RenewalListItem
              key={renewal.identifier ?? `renewal-${index}`}
              renewal={renewal}
              onClick={handleRenewalClick}
            />
          ))
        ) : (
          <Box sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No upcoming renewals found
            </Typography>
          </Box>
        )}
      </List>

      <AssetLifecycleManagementDetailDrawer
        open={detailDrawerOpen}
        handleClose={() => {
          setDetailDrawerOpen(false);
          setSelectedLifecycleEventData(undefined);
        }}
        selectedLifecycleEventData={selectedLifecycleEventData}
        setSelectedLifecycleEventData={setSelectedLifecycleEventData}
        hasAssetOrderDataManagePermission={hasAssetOrderDataManagePermission}
      />
    </WidgetWrapper>
  );
};

export default UpcomingRenewalsWidget;
